import { ApolloError } from '@apollo/client';

import { useGroupRosterQuery } from 'core/graphql';

import { IGroupRosterRow } from 'components/pages/GroupDetail/parts/rosterTypes';

import { useStableRefetch } from './useStableRefetch';

/**
 * Group roster data hook (Req 9.2–9.4).
 *
 * NOTE: single-batch, NOT paginated. Although task 9.1 is titled
 * "useGroupRoster (paginated)", schema verification in task 8.2 confirmed the
 * deployed `GroupType.agreements` field is a plain `[AgreementType!]!` list
 * with no relay `first`/`after` arguments. The generated `GroupRosterQuery`
 * therefore takes only `groupId`, so this wrapper fetches the whole roster in
 * one request (the design's documented single-batch fallback). Relay-style
 * `fetchMore`/`hasNextPage`/`loadMore` are intentionally dropped — there is no
 * pagination to drive. If the backend later exposes a paginated `agreements`
 * connection, this hook is the seam to add `fetchMore` behind the same
 * interface.
 *
 * Mirrors the established wrapper style in `useGroupTabsData.ts`:
 * `cache-and-network` fetch policy, `notifyOnNetworkStatusChange` so `loading`
 * is true during background refresh, `skip` when no `groupId`, and a stable
 * `{ rows, loading, error, refetch }` shape.
 */
interface IUseGroupRoster {
  rows: IGroupRosterRow[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

export const useGroupRoster = (groupId?: string): IUseGroupRoster => {
  const { data, loading, error, refetch } = useGroupRosterQuery({
    variables: { groupId: groupId || '' },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return {
    rows: (
      data?.group?.agreements ?? []
    ).filter((row): row is IGroupRosterRow => Boolean(row)),
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};
